<script setup lang="ts">
/**
 * Бейдж **системной** роли: администратор / пользователь.
 *
 * ⚠️ Не путать с `MemberRoleBadge` (владелец / наблюдатель — роль в конкретном
 * мероприятии) и с ролью участника из справочника («спортсмен», «тренер»),
 * которая не имеет отношения к правам вообще. Три разных понятия, три разных
 * компонента — именно чтобы их нельзя было перепутать в шаблоне.
 *
 * Роль приходит `string`, а не union `SystemRole`: компонент переживает
 * появление третьей роли — незнакомое значение просто печатается как есть.
 */
import { computed } from 'vue'
import type { BadgeVariants } from '@/components/ui/badge'
import { Badge } from '@/components/ui/badge'
import { SYSTEM_ROLE_LABELS } from '@/types/api'

const props = defineProps<{
  role: string
}>()

const VARIANTS: Record<string, BadgeVariants['variant']> = {
  SYSTEM_ROLE_ADMIN: 'default',
  SYSTEM_ROLE_USER: 'secondary',
}

const view = computed(() => {
  const label = SYSTEM_ROLE_LABELS[props.role as keyof typeof SYSTEM_ROLE_LABELS]
  return label && props.role !== 'SYSTEM_ROLE_UNSPECIFIED'
    ? { label, variant: VARIANTS[props.role] }
    : { label: props.role, variant: 'outline' as const }
})
</script>

<template>
  <Badge :variant="view.variant">
    {{ view.label }}
  </Badge>
</template>
